import React from 'react';
import { twMerge } from 'tailwind-merge';
import { applyTypography, BaseButton } from '@repo/common';

import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-top.svg';

const DEFAULT_PLACEHOLDER = 'Please select 000';

const resolveState = ({ state, value }) => {
  if (state === 'active') return 'active';
  if (state === 'selected' || value) return 'selected';
  return 'default';
};

const containerClassesByState = (state) =>
  twMerge(
    'relative flex h-20 w-full max-w-[278px] items-center gap-2 rounded-[15px] border-2 border-transparent bg-surface-lightgray60 px-6 py-4 transition-colors duration-200',
    state === 'active' ? 'border-2 border-surface-green40' : ''
  );

const textClassesByState = (state, displayText) =>
  twMerge(
    applyTypography('b16Strong', displayText),
    'min-w-0 flex-1 truncate text-left',
    state === 'default' ? 'text-text-placeholder' : 'text-green-deep02'
  );

const iconClassesByState = (state) =>
  twMerge(
    'size-5 shrink-0',
    state === 'active' ? 'text-green-deep02' : 'text-text-placeholder'
  );

/**
 * @example
 * <PathfinderDropdownSelectBar
 *   placeholder="Please select a track"
 *   onClick={handleOpen}
 * />
 *
 * @example
 * <PathfinderDropdownSelectBar
 *   value="Cultural Explorer"
 *   state="active"
 *   onClick={handleToggle}
 * />
 */

/**
 * Reusable dropdown trigger for Pathfinder flows.
 *
 * @component
 * @param {string} placeholder Text to show before a value is selected
 * @param {string} value Selected value to render in the trigger
 * @param {"default" | "active" | "selected"} state Visual state from Figma
 * @param {string} className Extra Tailwind classes for layout overrides
 * @param {("button" | "submit" | "reset")} type Native button type
 *
 * Reuse notes
 * - Use `state="active"` while the dropdown panel is open
 * - Use `value` for the selected label; when `value` exists, the trigger falls back to selected styling automatically
 * - Keep the options list outside this component so it can be reused with modal, popover, or inline menus
 */
function PathfinderDropdownSelectBar({
  placeholder = DEFAULT_PLACEHOLDER,
  value = '',
  state = 'default',
  className = '',
  type = 'button',
  ...props
}) {
  const resolvedState = resolveState({ state, value });
  const displayText = value || placeholder;
  const ArrowIcon = resolvedState === 'active' ? ArrowTopIcon : ArrowBottomIcon;

  return (
    <BaseButton
      type='text'
      size='m'
      htmlType={type}
      typo={false}
      isStatic
      leftalign
      gap='gap-2'
      icon={
        <ArrowIcon className={iconClassesByState(resolvedState)} aria-hidden />
      }
      iconPosition='after'
      aria-expanded={resolvedState === 'active'}
      className={twMerge(
        'min-w-0 cursor-pointer',
        containerClassesByState(resolvedState),
        className,
      )}
      textWrapperClass={twMerge(
        'h-auto',
        textClassesByState(resolvedState, displayText),
      )}
      {...props}
    >
      {displayText}
    </BaseButton>
  );
}

export default PathfinderDropdownSelectBar;
